import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { TaxTypeFragment } from 'generated/resources/graphql'

import MoneyField from 'modules/core/components/MoneyField'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useForm } from 'modules/core/hooks/useForm'
import {
  amountToNumber,
  fromCents,
  isValidAmount,
  toCents,
} from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import { Tax } from 'modules/expenses/hooks/useExpensesCustomization'
import { TaxRateSelector } from 'modules/finance/components/TaxRateSelector'
import { TaxTypeSelector } from 'modules/finance/components/TaxTypeSelector'

const personalIncomeKey = 'personal_income'

interface Props {
  tax: Tax
  amount: number
  currency: string
  mandatory?: boolean
  readonly?: boolean
  autofilled?: boolean
  onChange: (tax: Tax) => void
  onDelete: (tax: Tax) => void
  showErrors: boolean
  countryCode?: string
  taxTypes?: TaxTypeFragment[]
}

type Errors = { [key in keyof Tax]: string }

interface TaxErrors {
  tax: Tax
  amount: number
  readonly?: boolean
  baseAmountString: string
  amountString: string
}

const taxErrors = ({
  tax: { base_amount, percentage, amount, type },
  amount: document_amount,
  readonly,
  baseAmountString,
  amountString,
}: TaxErrors) => {
  const errors: Errors = {}

  if (readonly) return errors

  const isPersonalIncome = type === personalIncomeKey

  if (isNaN(Number(baseAmountString))) {
    errors.base_amount = i18n.t('expenses_v2.forms.errors.invalid')
  }

  if (isNaN(Number(amountString))) {
    errors.amount = i18n.t('expenses_v2.forms.errors.invalid')
  }

  if (base_amount == null) {
    errors.base_amount = i18n.t('expenses_v2.forms.errors.required')
  } else if (base_amount < 0) {
    errors.base_amount = i18n.t('expenses_v2.forms.errors.invalid')
  }

  if (percentage == null) {
    errors.percentage = i18n.t('expenses_v2.forms.errors.required')
  } else if (percentage < 0 || percentage > 100) {
    errors.percentage = i18n.t('expenses_v2.forms.errors.invalid')
  } else if (percentage === 0 && base_amount !== 0 && amount !== 0) {
    errors.amount = i18n.t('expenses_v2.forms.errors.invalid')
  }

  if (amount == null) {
    errors.amount = i18n.t('expenses_v2.forms.errors.required')
  } else if (
    base_amount != null &&
    ((isPersonalIncome && amount > 0) || (!isPersonalIncome && amount < 0))
  ) {
    errors.amount = i18n.t('expenses_v2.forms.errors.invalid')
  } else if (amount == 0 && base_amount !== 0 && percentage !== 0) {
    errors.amount = i18n.t('expenses_v2.forms.errors.invalid')
  }

  return errors
}

const TaxFieldsSelector = ({
  tax,
  amount,
  currency,
  readonly,
  autofilled,
  onChange,
  onDelete,
  showErrors,
  countryCode,
  taxTypes,
}: Props) => {
  const form = useForm<Tax>({
    defaultValues: {
      id: tax.id || '',
      base_amount: tax.base_amount || 0,
      percentage: tax.percentage || 0,
      amount: tax.amount || 0,
      type: tax.type,
      tax_type_id: tax.tax_type_id || 0,
      tax_type_name: tax.tax_type_name || '',
      tax_rates: tax.tax_rates || [],
    },
    onSubmit: (data) => {
      return Promise.resolve({ success: true, data: data })
    },
  })

  const [baseAmountString, setBaseAmountString] = React.useState<string>(
    fromCents(tax.base_amount || 0).toString()
  )
  const [amountString, setAmountString] = React.useState<string>(
    fromCents(tax.amount || 0).toString()
  )

  const [errors, setErrors] = React.useState<Errors>({})
  const [autofilledFields, setAutofilledFields] = React.useState<string[]>([])

  const calculateAmount = useCallback(
    (tax: Tax) => {
      if (typeof tax.base_amount !== 'number' || isNaN(tax.base_amount)) {
        return 0
      }

      const baseAmount = tax.base_amount ?? 0
      const percentage = fromCents(tax.percentage ?? 0)

      const amount = Math.round(baseAmount * percentage)
      return form.values.type == personalIncomeKey ? amount * -1 : amount
    },
    [form.values.type]
  )

  const handleBaseAmountChange = (value: string) => {
    setBaseAmountString(value)
    const newBaseAmount = toCents(Number(value))
    const amount = calculateAmount({
      base_amount: newBaseAmount,
      percentage: form.values.percentage,
    })
    setAmountString(fromCents(amount).toString())
    const newValues = { ...form.values, amount: amount, base_amount: newBaseAmount }
    form.setValues(newValues)
    onChange(newValues)
  }

  const handlePercentageChange = (value: string) => {
    const newPercentage = Number(value)
    form.setValues({ ...form.values, percentage: newPercentage })
    const percentage = isValidAmount(value) ? amountToNumber(value) : null
    const baseAmount = form.values.base_amount ?? 0
    let amount = 0

    if (percentage != null) {
      amount = calculateAmount({
        base_amount: baseAmount,
        percentage,
      })
    }
    setAmountString(fromCents(amount).toString())
    const newValues = { ...form.values, percentage: newPercentage, amount: amount }
    form.setValues(newValues)
    onChange(newValues)
  }

  const handleAmountChange = (value: string) => {
    setAmountString(value)
    const newValues = { ...form.values, amount: toCents(Number(value)) }
    form.setValues(newValues)
    onChange(newValues)
  }

  const handleTaxTypeChange = (taxType?: TaxTypeFragment) => {
    const matchingTaxRate = taxType?.taxRates.find(
      (taxRate) => Number(taxRate.rate * 100) === Number(form.values.percentage)
    )

    const percentage = isValidAmount(form.values.percentage?.toString() || '')
      ? amountToNumber(form.values.percentage)
      : null
    const baseAmount = form.values.base_amount ?? 0
    let amount = 0

    if (percentage != null) {
      amount = calculateAmount({
        base_amount: baseAmount,
        percentage,
      })
    }
    setAmountString(fromCents(amount).toString())
    const newValues = {
      ...form.values,
      tax_type_id: taxType?.id || null,
      tax_type_name: taxType?.name || null,
      type: taxType?.type || null,
      tax_rates: taxType?.taxRates.map((taxRate) => ({
        percentage: taxRate.rate,
      })),
      amount: amount,
      percentage: matchingTaxRate ? form.values.percentage : null,
    }
    form.setValues(newValues)
    onChange(newValues)
  }

  useUnsafeEffect(() => {
    if ('amount' in tax || 'percentage' in tax || showErrors) {
      setErrors(taxErrors({ tax, amount, readonly, baseAmountString, amountString }))
    }
  }, [tax, amount, readonly, showErrors, baseAmountString, amountString])

  useUnsafeEffect(() => {
    if (autofilled) {
      const amount = calculateAmount(tax)
      form.setValues({ ...tax, amount })
      setAutofilledFields(['base_amount', 'percentage', 'amount'])
    }
  }, [form, autofilled, tax, calculateAmount])

  return (
    <>
      <Box flexDirection="row" gap="s24">
        <Box width="full">
          <TaxTypeSelector
            currentTaxTypeId={form.values.tax_type_id}
            disabled={readonly ?? false}
            onSelect={handleTaxTypeChange}
            countryCode={countryCode}
            taxTypes={taxTypes}
          />
        </Box>

        <Box width="full">
          <TaxRateSelector
            currentTaxRate={form.values.percentage}
            taxRates={tax.tax_rates}
            disabled={readonly ?? false}
            onSelect={handlePercentageChange}
          />
        </Box>
      </Box>

      <Box flexDirection="row" gap="s16">
        <Box width="full">
          <MoneyField
            mandatory
            readonly={readonly}
            error={errors.base_amount}
            label={i18n.t('expenses_v2.forms.fields.taxes.label_base_amount')}
            currency={currency}
            placeholder="0"
            value={baseAmountString}
            autofilled={autofilledFields.includes('base_amount')}
            onChange={handleBaseAmountChange}
          />
        </Box>
        <Box width="full" flexDirection="row" gap="s16">
          <MoneyField
            mandatory
            readonly={readonly}
            error={errors.amount}
            label={i18n.t('expenses_v2.forms.fields.taxes.label_amount')}
            currency={currency}
            placeholder="0"
            value={amountString}
            autofilled={autofilledFields.includes('amount')}
            onChange={handleAmountChange}
          />

          <Box paddingTop="s32" alignItems="flexStart">
            {!readonly && (
              <Button type="secondary" onClick={() => onDelete(tax)} iconName="delete" />
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default TaxFieldsSelector
